import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Users, Zap, Shield, Wifi, Coffee, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Vehicle Showcase — the real GO VIP Peugeot E-5008 GT.
 * Rotating gallery of 3 real photos (front / rear / interior) uploaded by the client.
 * Auto-advance every 6 seconds, pauses on hover, dots + arrows for manual control.
 *
 * TO ADD MORE PHOTOS: append entries to VEHICLE_PHOTOS array.
 * Place new files under /app/frontend/public/vehicles/
 */
const VEHICLE_PHOTOS = [
  { src: '/vehicles/e5008-airport-front.jpg', labelKey: 'airport_departures' },
  { src: '/vehicles/e5008-airport-side.jpg', labelKey: 'airport_ready' },
  { src: '/vehicles/e5008-airport-trunk.jpg', labelKey: 'cargo_space' },
  { src: '/vehicles/e5008-gt-front.jpg', labelKey: 'exterior_front' },
  { src: '/vehicles/e5008-gt-rear.jpg', labelKey: 'exterior_rear' },
  { src: '/vehicles/e5008-gt-interior.jpg', labelKey: 'interior' },
];
const AUTO_INTERVAL = 6000;

export const VehicleShowcase = () => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = VEHICLE_PHOTOS.length;

  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);
  const prev = () => setIndex((i) => (i - 1 + total) % total);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, AUTO_INTERVAL);
    return () => clearInterval(id);
  }, [next, paused]);

  const specs = [
    { icon: Users, key: 'capacity' },
    { icon: Zap, key: 'engine' },
    { icon: Shield, key: 'safety' },
    { icon: Wifi, key: 'wifi' },
    { icon: Coffee, key: 'comfort' },
    { icon: Sparkles, key: 'premium' },
  ];

  const current = VEHICLE_PHOTOS[index];

  return (
    <section
      id="vehicle"
      className="relative overflow-hidden bg-gradient-to-b from-white via-amber-50/30 to-white py-20 md:py-28"
      data-testid="vehicle-section"
    >
      <div className="container mx-auto max-w-6xl px-4">
        {/* Header */}
        <div className="mb-12 text-center md:text-left">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-amber-100 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-amber-800">
            <Sparkles className="h-3 w-3" />
            {t('vehicle.kicker')}
          </div>
          <h2 className="mb-4 font-serif text-3xl font-bold leading-tight text-gray-900 md:text-5xl">
            {t('vehicle.title')}
          </h2>
          <p className="mx-auto max-w-2xl text-base leading-relaxed text-gray-600 md:mx-0 md:text-lg">
            {t('vehicle.subtitle')}
          </p>
        </div>

        {/* Gallery + Specs */}
        <div className="grid gap-8 lg:grid-cols-5 lg:items-start">
          {/* Gallery (3/5) */}
          <div
            className="lg:col-span-3"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <div className="group relative overflow-hidden rounded-2xl shadow-2xl ring-1 ring-black/10 bg-black">
              {/* Image stack (fade transition) */}
              <div className="relative aspect-[4/5] md:aspect-[4/3] w-full">
                {VEHICLE_PHOTOS.map((photo, i) => (
                  <img
                    key={photo.src}
                    src={photo.src}
                    alt={`Peugeot E-5008 GT — ${t(`vehicle.gallery.${photo.labelKey}`)}`}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out ${
                      i === index ? 'opacity-100' : 'opacity-0'
                    }`}
                    data-testid={`vehicle-photo-${photo.labelKey}`}
                  />
                ))}

                {/* Bottom gradient + label */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent p-5 md:p-8">
                  <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.25em] text-amber-300 md:text-xs">
                    {t(`vehicle.gallery.${current.labelKey}`)}
                  </p>
                  <p className="font-serif text-xl font-bold text-white md:text-3xl">
                    Peugeot E-5008 GT
                  </p>
                </div>

                {/* Prev / Next arrows */}
                <button
                  onClick={prev}
                  aria-label="Previous photo"
                  className="absolute left-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white opacity-0 backdrop-blur-md transition-all hover:bg-white/30 group-hover:opacity-100"
                  data-testid="vehicle-prev"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={next}
                  aria-label="Next photo"
                  className="absolute right-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white opacity-0 backdrop-blur-md transition-all hover:bg-white/30 group-hover:opacity-100"
                  data-testid="vehicle-next"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>

                {/* Progress dots */}
                <div className="absolute bottom-3 right-3 flex gap-1.5">
                  {VEHICLE_PHOTOS.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setIndex(i)}
                      aria-label={`View photo ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === index ? 'w-6 bg-amber-400' : 'w-1.5 bg-white/60 hover:bg-white'
                      }`}
                      data-testid={`vehicle-dot-${i}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Thumbnails */}
            <div className="mt-3 grid grid-cols-3 gap-2 md:grid-cols-6 md:gap-3">
              {VEHICLE_PHOTOS.map((photo, i) => (
                <button
                  key={photo.src}
                  onClick={() => setIndex(i)}
                  className={`group relative overflow-hidden rounded-lg ring-2 transition-all ${
                    i === index
                      ? 'ring-amber-500 shadow-lg'
                      : 'ring-gray-200 hover:ring-amber-300 opacity-70 hover:opacity-100'
                  }`}
                  data-testid={`vehicle-thumb-${i}`}
                >
                  <img
                    src={photo.src}
                    alt={t(`vehicle.gallery.${photo.labelKey}`)}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Specs (2/5) */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {specs.map(({ icon: Icon, key }) => (
                <div
                  key={key}
                  className="group rounded-xl border border-gray-200 bg-white p-4 transition-all hover:border-amber-300 hover:shadow-lg"
                  data-testid={`vehicle-spec-${key}`}
                >
                  <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-700 ring-1 ring-amber-100 transition-all group-hover:bg-amber-500 group-hover:text-white">
                    <Icon className="h-4 w-4" />
                  </div>
                  <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-amber-700">
                    {t(`vehicle.specs.${key}.label`)}
                  </p>
                  <p className="text-sm font-medium leading-tight text-gray-900">
                    {t(`vehicle.specs.${key}.value`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VehicleShowcase;
